import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { Button, Span } from "@icg360/design-system";

import { AuthAppContext } from "components/root/auth-app-provider";
import { INSPECTION_ENUM_STATUS_MAP, type InspectionEnum } from "consts";
import { useInspectionsData } from "pages/inspections/hooks";
import {
  type BadgeProps,
  WelcomeBannerSection,
} from "pages/overview/welcome-banner/welcome-banner-section";
import { formatDate, trackEvent } from "utils";
import { type KeystoneInspection } from "utils/keystone-api-mapping";

type StatusContent = {
  badgeProps?: BadgeProps;
  body: ReactNode;
  detailsButtonPath?: string;
  detailsButtonEventDetails?: string;
  buttonText?: string;
};

const getStatusContent = (
  policyType: string,
  effectiveDate: string,
  inspectionDueDate?: string,
  latestInspection?: KeystoneInspection,
  statusEnum?: number
): StatusContent => {
  const issuesFoundBadgeText = "Issues found";

  // H06 policies don't require inspections
  if (policyType === "H06") {
    return {
      body: "Your policy does not require inspection. For any questions or concerns, please contact SageSure.",
    };
  }

  const contentMap: Record<InspectionEnum, StatusContent> = {
    100: {
      badgeProps: { appearance: "neutral", text: "Pending" },
      body: "An inspection has been ordered for your home. Please wait for completion and an inspection review by SageSure.",
    },
    110: {
      badgeProps: { appearance: "neutral", text: "Pending" },
      body: "An inspection has been ordered for your home. Please wait for completion and an inspection review by SageSure.",
    },
    200: {
      body: (
        <div>
          If your policy requires an inspection, SageSure will order the
          inspection near the effective date of{" "}
          <Span bold size="sm">
            {formatDate(effectiveDate)}
          </Span>
          .
        </div>
      ),
    },
    210: {
      badgeProps: { appearance: "neutral", text: "Pending" },
      body: "Your inspection couldn’t be completed and a new one is required immediately. See the related email from SageSure for instructions. For any questions or concerns, please contact SageSure.",
    },
    300: {
      badgeProps: { appearance: "neutral", text: "In-review" },
      body: "We’ve ordered an inspection to ensure everything is in good shape. Once it’s reviewed, your insurance rep will reach out if anything needs your attention.",
    },
    400: {
      badgeProps: { appearance: "success", text: "Complete" },
      body: "Everything looks good in your home inspection and your policy is ready to go. Welcome to SageSure!",
    },
    410: {
      badgeProps: { appearance: "success", text: "Complete" },
      body: "We made a few updates to your policy based on your home inspection review.",
      detailsButtonPath: "/my/documents",
      buttonText: "View in Documents",
    },
    420: {
      badgeProps: { appearance: "warning", text: issuesFoundBadgeText },
      body: "Based on our inspection, repairs or updates are needed. Your coverage is at risk until these issues are resolved.",
      detailsButtonPath: "/my/inspections",
      detailsButtonEventDetails: `${latestInspection?.type} Write Out`,
    },
    430: {
      badgeProps: { appearance: "warning", text: issuesFoundBadgeText },
      body: "Based on our inspection, repairs or updates are needed. Your coverage is at risk until these issues are resolved.",
      detailsButtonPath: "/my/inspections",
      detailsButtonEventDetails: `${latestInspection?.type} Write Out`,
    },
    440: {
      badgeProps: { appearance: "warning", text: issuesFoundBadgeText },
      body: (
        <div>
          Based on our inspection, repairs or updates are needed
          {inspectionDueDate ? (
            <Span size="sm">
              {" "}
              by{" "}
              <Span bold size="sm">
                {formatDate(inspectionDueDate)}
              </Span>
            </Span>
          ) : (
            ""
          )}
          . Your coverage is at risk until these issues are resolved.
        </div>
      ),
      detailsButtonPath: "/my/inspections",
      detailsButtonEventDetails: `${latestInspection?.type} Write Out`,
    },
    445: {
      body: "Unfortunately, your property doesn’t meet the inspection guidelines for this policy. You might be eligible for different options. Contact your insurance rep for next steps.",
    },
    450: {
      badgeProps: { appearance: "success", text: "Complete" },
      body: "Thank you for addressing the underwriting concerns from SageSure—no further action is required. Welcome to SageSure!",
    },
    460: {
      body: "Your policy was cancelled for non-underwriting reasons. Contact your agent if you have any questions or concerns.",
    },
    480: {
      badgeProps: { appearance: "danger", text: "Ineligible" },
      body: "Unfortunately, your property doesn’t meet the inspection guidelines for this policy. You might be eligible for different options. Contact your insurance rep for next steps.",
      detailsButtonEventDetails: `${latestInspection?.type} Write Out - Ineligible`,
      detailsButtonPath: "/my/inspections",
    },
    1000: {
      badgeProps: { appearance: "neutral", text: "Not found" },
      body: "We weren’t able to find your inspection status. Please contact your agent if you have any questions or concerns.",
    },
    1010: {
      body: "We don't have any inspection updates to share. We'll let you know if anything changes.",
    },
    1020: {
      body: "We don't have any inspection updates to share. We'll let you know if anything changes.",
    },
    1030: {
      badgeProps: { appearance: "neutral", text: "Not found" },
      body: "We weren’t able to find your inspection status. Please contact your agent if you have any questions or concerns.",
    },
    1040: {
      badgeProps: { appearance: "neutral", text: "Not found" },
      body: "We weren’t able to find your inspection status. Please contact your agent if you have any questions or concerns.",
    },
  };

  const unknownStatusContent: StatusContent = {
    body: "Looks like we’re having trouble pulling up your inspection info. Reach out to your insurance rep if you need help.",
  };

  return contentMap[statusEnum ?? 0] ?? unknownStatusContent;
};

export const UnderwritingSection = () => {
  const navigate = useNavigate();
  const { isPolicyDataReady, userDetails, userPolicyProperties } =
    useContext(AuthAppContext);
  const [statusContent, setStatusContent] = useState<StatusContent>({
    body: "",
  });
  const {
    inspectionDueDate,
    latestInspection,
    isLoading: loading,
  } = useInspectionsData();

  useEffect(() => {
    if (latestInspection) {
      trackEvent("welcomeTaskCard_underwriting_load", {
        enum: latestInspection?.status,
        status: INSPECTION_ENUM_STATUS_MAP[latestInspection?.status ?? 0],
      });
    }
  }, [latestInspection]);

  useEffect(() => {
    if (isPolicyDataReady && latestInspection && userDetails) {
      const policyType = userPolicyProperties?.policyType ?? "";
      const effectiveDate = userDetails.currentTerm?.effectiveDate;
      const statusEnum = latestInspection?.status;
      setStatusContent(
        getStatusContent(
          policyType,
          effectiveDate,
          inspectionDueDate,
          latestInspection,
          statusEnum
        )
      );
    }
  }, [
    inspectionDueDate,
    isPolicyDataReady,
    latestInspection,
    userDetails,
    userPolicyProperties?.policyType,
  ]);

  const handleViewDetailsClick = useCallback(() => {
    trackEvent("welcomeTaskCard_underwriting_viewDetails", {
      inspectionStatus: latestInspection?.status,
      details: statusContent.detailsButtonEventDetails,
    });
    if (statusContent?.detailsButtonPath) {
      navigate(statusContent?.detailsButtonPath);
    }
  }, [
    navigate,
    latestInspection?.status,
    statusContent.detailsButtonPath,
    statusContent.detailsButtonEventDetails,
  ]);

  return (
    <WelcomeBannerSection
      title="Property inspection"
      loading={loading}
      badgeProps={statusContent.badgeProps}
      footer={
        statusContent.detailsButtonPath ? (
          <Button
            appearance="secondary"
            size="sm"
            onPress={handleViewDetailsClick}
            data-testid="view-inspection-review-btn"
          >
            {statusContent.buttonText ?? "View details"}
          </Button>
        ) : null
      }
    >
      {statusContent.body}
    </WelcomeBannerSection>
  );
};
